/**
 * <slide-drawer> — shared slide out navigation drawer.
 *
 * Used by both the EXPLORE menu and the SHOP (hamburger) menu so the two behave and look identical.
 * The drawer owns its overlay, so it never depends on elements rendered later in the document.
 *
 * Attributes:
 *   data-position="left|right"     side the panel slides in from
 *   data-toggle="<css selector>"   optional external toggle (the header hamburger button); clicks are
 *                                  delegated from the document so DOM order does not matter
 */
if (!customElements.get('slide-drawer')) {
  customElements.define(
    'slide-drawer',
    class SlideDrawer extends HTMLElement {
      connectedCallback() {
        this.panel = this.querySelector('.slide-drawer');
        this.overlay = this.querySelector('.slide-drawer__overlay');
        this.button = this.querySelector('.slide-drawer__toggle');
        this.externalToggleSelector = this.dataset.toggle || null;

        if (!this.panel) return;

        this.onDocumentClick = this.onDocumentClick.bind(this);
        this.onKeyDown = this.onKeyDown.bind(this);

        this.button?.addEventListener('click', (event) => {
          event.preventDefault();
          this.open ? this.close() : this.show();
        });

        this.querySelector('.slide-drawer__close')?.addEventListener('click', () => this.close());
        this.overlay?.addEventListener('click', () => this.close());
        document.addEventListener('keydown', this.onKeyDown);

        if (this.externalToggleSelector) document.addEventListener('click', this.onDocumentClick);

        // Following a link leaves the page anyway, but close first so anchors and same page links behave.
        this.panel.querySelectorAll('a[href]').forEach((link) => {
          link.addEventListener('click', () => this.close());
        });

        this.querySelectorAll('.slide-drawer__trigger').forEach((trigger) => {
          trigger.addEventListener('click', () => this.toggleSection(trigger));
        });

        // Tabbed drawer (mobile menu): one tab per menu, each switching the panel below it.
        this.tabs = Array.from(this.querySelectorAll('.slide-drawer__tab'));
        this.panels = Array.from(this.querySelectorAll('.slide-drawer__panel'));
        this.tabs.forEach((tab) => tab.addEventListener('click', () => this.selectTab(tab)));

        this.setInert(true);

        // Re-parent last: the drawer may be rendered inside the header, which is transformed when sticky. A
        // transformed ancestor becomes the containing block for `position: fixed` and its own stacking context,
        // which pins the panel below the announcement bar and traps the overlay under the header. Listeners are
        // bound above, on the elements themselves, so they survive the move — but any query rooted at `this`
        // would not, which is why nothing is looked up after this point.
        document.body.appendChild(this.panel);
        if (this.overlay) document.body.appendChild(this.overlay);
      }

      disconnectedCallback() {
        document.removeEventListener('keydown', this.onKeyDown);
        document.removeEventListener('click', this.onDocumentClick);
        this.unlockScroll();

        // The panel and overlay live on <body>, so they have to be cleaned up by hand (theme editor re-renders).
        this.panel?.remove();
        this.overlay?.remove();
      }

      get open() {
        return this.classList.contains('is-open');
      }

      onDocumentClick(event) {
        const toggle = event.target.closest(this.externalToggleSelector);
        if (!toggle) return;

        event.preventDefault();
        this.externalToggle = toggle;
        this.open ? this.close() : this.show();
      }

      get activeToggle() {
        return this.button || this.externalToggle || null;
      }

      show() {
        // Only one drawer at a time.
        document.querySelectorAll('slide-drawer').forEach((drawer) => {
          if (drawer !== this && drawer.close) drawer.close();
        });

        if (this.overlay) this.overlay.hidden = false;
        // Let the overlay paint before the transition starts.
        requestAnimationFrame(() => {
          this.classList.add('is-open');
          this.panel.classList.add('is-open');
          this.overlay?.classList.add('is-open');
        });

        this.activeToggle?.setAttribute('aria-expanded', 'true');
        this.setInert(false);
        this.lockScroll();

        clearTimeout(this.hideOverlayTimeout);
        this.panel.focus({ preventScroll: true });
      }

      close() {
        if (!this.open) return;

        this.classList.remove('is-open');
        this.panel.classList.remove('is-open');
        this.overlay?.classList.remove('is-open');
        this.activeToggle?.setAttribute('aria-expanded', 'false');
        this.setInert(true);
        this.unlockScroll();

        // Keep the overlay around until the fade out finishes, then take it out of the hit area.
        clearTimeout(this.hideOverlayTimeout);
        this.hideOverlayTimeout = setTimeout(() => {
          if (!this.open && this.overlay) this.overlay.hidden = true;
        }, 450);
      }

      setInert(value) {
        this.panel.setAttribute('aria-hidden', value ? 'true' : 'false');
        if ('inert' in HTMLElement.prototype) this.panel.inert = value;
      }

      lockScroll() {
        this.scrollLocked = true;
        document.documentElement.style.overflow = 'hidden';
      }

      unlockScroll() {
        if (!this.scrollLocked) return;
        this.scrollLocked = false;
        document.documentElement.style.overflow = '';
      }

      selectTab(tab) {
        const panelId = tab.getAttribute('aria-controls');

        this.tabs.forEach((other) => {
          const isActive = other === tab;
          other.classList.toggle('is-active', isActive);
          other.setAttribute('aria-selected', isActive ? 'true' : 'false');
        });

        this.panels.forEach((panel) => {
          const isActive = panel.id === panelId;
          panel.classList.toggle('is-active', isActive);
          panel.hidden = !isActive;
        });

        // The body scrolls as one, so a tab switch should start at the top of the new menu.
        const body = this.panels[0]?.parentElement;
        if (body) body.scrollTop = 0;
      }

      toggleSection(trigger) {
        const panel = trigger.nextElementSibling;
        if (!panel || !panel.classList.contains('slide-drawer__collapsible')) return;

        const isOpen = trigger.getAttribute('aria-expanded') === 'true';

        // Accordion: only one section open per level.
        Array.from(trigger.closest('.slide-drawer__menu').children).forEach((item) => {
          const other = item.querySelector(':scope > .slide-drawer__trigger');
          if (other && other !== trigger && other.getAttribute('aria-expanded') === 'true') {
            this.collapse(other, other.nextElementSibling);
          }
        });

        isOpen ? this.collapse(trigger, panel) : this.expand(trigger, panel);
      }

      expand(trigger, panel) {
        trigger.setAttribute('aria-expanded', 'true');
        panel.style.height = `${panel.scrollHeight}px`;
        // Release the fixed height once open so nested sections can grow the panel.
        panel.addEventListener('transitionend', function onEnd(event) {
          if (event.propertyName !== 'height') return;
          panel.removeEventListener('transitionend', onEnd);
          if (trigger.getAttribute('aria-expanded') === 'true') panel.style.height = 'auto';
        });
      }

      collapse(trigger, panel) {
        if (!panel) return;
        trigger.setAttribute('aria-expanded', 'false');
        panel.style.height = `${panel.scrollHeight}px`;
        panel.offsetHeight; // force reflow so the transition runs from a real height
        panel.style.height = '0px';
      }

      onKeyDown(event) {
        if (event.key !== 'Escape' || !this.open) return;
        this.close();
        this.activeToggle?.focus();
      }
    }
  );
}
